/**
 * https://developer.chrome.com/extensions/match_patterns
 */
const schemeSeparator = '://';

const ipV6Regex = /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]+|::(ffff(:0{1,4})?:)?((25[0-5]|(2[0-4]|1?[0-9])?[0-9])\.){3}(25[0-5]|(2[0-4]|1?[0-9])?[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1?[0-9])?[0-9])\.){3}(25[0-5]|(2[0-4]|1?[0-9])?[0-9]))$/;//eslint-disable-line

// derived from https://source.chromium.org/chromium/chromium/src/+/master:url/url_canon_host.cc;l=53;drc=abf7a4e9b3d2151e0c5c05cf8603e9029f4e006d;bpv=1;bpt=1
const invalidCharLookup = [
    // form source code of chrome
    ...Array.from({ length: 32 }, (x, i) => i),
    37, 47, 59, 63, 92, 94,

    // found experimentally run:
    // (new Array(256)).fill(0).map((_, i) => i + 1).forEach(i => chrome.webRequest.onBeforeRequest.addListener(() => null, { urls: [`*://test${String.fromCharCode(i)}/${i}`] }))
    ...Array.from({ length: 160 - 126 }, (x, i) => i + 126),

    // `[` and `]` are allowed for ipv6 hosts but this parser does not support it
    91, 93,
].reduce((acc, charCode) => {
    acc[charCode] = 1;
    return acc;
}, {});

// run new Array(1000).fill(0).map((_, i) => String.fromCharCode(i)).filter((c) => { try { new URL(`http://${c}/`); } catch (e) { return true } })
// in chrome and firefox. following are the symbols that are allowed in chrome but not in firefox
const symbolsChromeAllowsInHost = [
    32, 34, 42, 60, 62, 124, 160, 168, 175, 180,
    184, 728, 729, 730, 731, 732, 733, 890, 900, 901,
].map((charCode) => String.fromCharCode(charCode));

const maxHostLength = 253;

export function parseUrlPattern(patternStr) {
    const schemeEndPos = patternStr.indexOf(schemeSeparator);
    if (schemeEndPos === -1) {
        return null;
    }

    const scheme = patternStr.substring(0, schemeEndPos).toLowerCase();
    if (scheme !== '*' && scheme !== 'http' && scheme !== 'https') {
        return null;
    }

    const pathStartPos = patternStr.indexOf('/', schemeEndPos + schemeSeparator.length);
    if (pathStartPos === -1) {
        return null;
    }

    let host;
    let port;
    const hostAndPort = patternStr.substring(schemeEndPos + schemeSeparator.length, pathStartPos);
    const portStartPos = hostAndPort.lastIndexOf(':');
    if (portStartPos !== -1) {
        port = hostAndPort.substring(portStartPos + 1);
        if (!port || isNaN(port)) {
            return null;
        }
        host = hostAndPort.substring(0, portStartPos);
    } else {
        host = hostAndPort;
    }

    if (host !== '*') {
        let isIpV6 = false;
        const hostWithoutWildcard = host.startsWith('*.') ? host.substring(2) : host;
        if (!hostWithoutWildcard || (hostWithoutWildcard.length > maxHostLength)) {
            return null;
        }

        if (hostWithoutWildcard.indexOf('*') !== -1) {
            return null;
        }

        if (hostWithoutWildcard.split('').some((c) => invalidCharLookup[c.charCodeAt(0)])) {
            return null;
        }

        if (hostWithoutWildcard.indexOf(':') !== -1) {
            if (!ipV6Regex.test(hostAndPort)) {
                return null;
            }
            isIpV6 = true;
        }

        // experiments showed that hostname validation in URL constructor is same as chrome pattern
        if (window.URL && !isIpV6) {
            // firefox URL validator is stricter than chrome's, we don't care and work around it
            const hostToValidate = hostWithoutWildcard.split('')
                .filter((c) => !symbolsChromeAllowsInHost.includes(c)).join('');
            if (!(hostToValidate.length === 0 && hostWithoutWildcard.length > 0)) {
                try {
                    // eslint-disable-next-line no-new
                    new window.URL(`http://${hostToValidate}/`);
                } catch (e) {
                    return null;
                }
            }
        }
    }

    const path = patternStr.substring(pathStartPos + 1);

    return {
        scheme,
        host,
        port,
        path,
        patternWithoutPort: `${scheme}://${host}/${path}`,
    };
}

export default function urlPatternValidator(str) {
    return !!str && (parseUrlPattern(str) !== null);
}

export function urlSimpleSchemeValidator(url) {
    const regex = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|(www\.)?[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|(www\.)?[a-zA-Z0-9]+\.[^\s]{2,})/;//eslint-disable-line
    return !!url && regex.test(url);
}

export function idpUrlSimpleSchemeValidator(url) {
    const regex = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/;//eslint-disable-line
    return !!url && regex.test(url);
}

export function normalizeUrlPattern(url = '') {
    let fixedUrl = '';
    try {
        const { scheme, port, host, path } = parseUrlPattern(url);
        fixedUrl = `${scheme.toLowerCase()}://${host.toLowerCase()}${port ? ':' + port : ''}/${path}`;
    } catch (error) {
        /* ignore */
    }
    return fixedUrl || url.toLowerCase() || '';
}
